import { formatReadableNumber } from '@lfg/core/lib';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { useState } from 'react';

import { DataTable } from 'src/components/DataTable';
import { AppPagination } from 'src/components/Pagination';
import { colors } from 'src/global/styles/colors';
import { useAnalyticsContext } from 'src/hooks/useAnalyticsContext';
import { useGetProjectStatisticsQuery } from 'src/store/services/analytics.service';

const headerCellStyle = {
  fontSize: '14px',
  fontWeight: 400,
  color: colors.neutral90,
  fontFamily: 'Gotham',
  borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
  padding: '12px 16px',
};

const dataCellStyle = {
  fontSize: '14px',
  fontWeight: 400,
  color: colors.white,
  fontFamily: 'Gotham',
  borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
  padding: '12px 16px',
};

export function ProjectStatisticsTable() {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { address, network } = useAnalyticsContext();

  const {
    data: projectStats,
    isLoading,
    error,
  } = useGetProjectStatisticsQuery(
    {
      page,
      size: rowsPerPage,
      address,
      network,
    },
    { skip: !address || !network },
  );

  const calculatePercentage = (revenue: number, totalRevenue: number) => {
    if (totalRevenue === 0) return '0%';
    return `${((revenue / totalRevenue) * 100).toFixed(1)}%`;
  };

  const totalRevenue = projectStats?.data?.reduce((sum, project) => sum + project.revenues, 0) || 0;

  const startIndex = (page - 1) * rowsPerPage;

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
      }}>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: colors.white,
          fontFamily: 'Gotham',
          marginBottom: 3,
        }}>
        Project Statistics
      </Typography>

      <DataTable
        renderHeader={() => (
          <TableRow>
            <TableCell sx={{ ...headerCellStyle, width: '40px' }}>#</TableCell>
            <TableCell sx={{ ...headerCellStyle, width: '380px' }}>Project</TableCell>
            <TableCell sx={{ ...headerCellStyle }}>Total Nodes Sold</TableCell>
            <TableCell sx={{ ...headerCellStyle }}>Revenue</TableCell>
            <TableCell sx={{ ...headerCellStyle, width: '280px' }}>Percentage of Total Sales</TableCell>
          </TableRow>
        )}
        renderBody={() => (
          <>
            {projectStats?.data.map((row, index) => (
              <TableRow key={row.sale}>
                <TableCell sx={{ ...dataCellStyle, textAlign: 'center' }}>
                  <Typography fontSize='14px'>{startIndex + index + 1}</Typography>
                </TableCell>
                <TableCell sx={dataCellStyle}>
                  <Typography fontSize='14px'>{row.name || 'Unknown Project'}</Typography>
                </TableCell>
                <TableCell sx={dataCellStyle}>
                  <Typography fontSize='14px'>{row.nodeSold.toLocaleString()}</Typography>
                </TableCell>
                <TableCell sx={dataCellStyle}>
                  <Typography fontSize='14px'>
                    {formatReadableNumber(row.revenues, { maximumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell sx={dataCellStyle}>
                  <Typography fontSize='14px'>{calculatePercentage(row.revenues, totalRevenue)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </>
        )}
        isLoading={isLoading}
        skeletonRowCount={5}
        isEmpty={!projectStats?.data?.length}
      />
      <Stack direction={'row'} justifyContent={'end'} marginTop={2}>
        <AppPagination
          count={projectStats?.meta?.total || 0}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={e => setRowsPerPage(Number(e.target.value))}
          sx={{
            border: 'none',
          }}
        />
      </Stack>

      {!!error && (
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              color: colors.red200,
              textAlign: 'center',
            }}>
            Failed to load project statistics. Please try again.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
